/**
 * Presentational component for selecting a camera with scrollable dropdown.
 */

import type { Camera } from '../../types/Camera';
import DropdownFilter from '../dropdowns/DropdownFilter'; // Use DropdownFilter for scrolling
import { useCamera } from '../../hooks/useCamera';

export default function CameraSelector(): JSX.Element {
  const { availableCameras, selectedCamera, handleSelectedCameraChange } =
    useCamera();

  if (selectedCamera === null) {
    return <></>;
  }

  // Natural sort for camera options, similar to DropdownLocationSingle
  const naturalSortOptions = (options: Array<{ id: string; value: string }>) => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    return [...options].sort((a, b) => collator.compare(a.value, b.value));
  };

  return (
    <DropdownFilter
      title={selectedCamera.displayName} // Display selected camera name as title
      options={naturalSortOptions(
        availableCameras.map((camera: Camera) => ({
          id: camera.id,
          value: camera.displayName,
        }))
      )}
      selected={[
        {
          id: selectedCamera.id,
          value: selectedCamera.displayName,
        },
      ]}
      handleChange={(id: string) => {
        const camera = availableCameras.find(
          (camera: Camera) => camera.id === id
        );
        if (camera === undefined) {
          return;
        }
        handleSelectedCameraChange(camera.id);
      }}
      filterFunction={() => {}} // Placeholder filter function, adjust as needed
      dataCy="camera-selector"
    />
  );
}